import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { extractVariables } from '../../../services/api';

const StepPrompts = ({
  formData,
  updateFormData
}) => {
  const [detectedVars, setDetectedVars] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);

  const showSystemPrompt = formData.prompt_type === 'System User' || formData.prompt_type === 'System  User  Assistant ';
  const showFewShot = formData.prompt_type === 'System  User  Assistant ';
  const isSystemPromptRequired = showSystemPrompt;

  useEffect(() => {
    const extractVars = async () => {
      if (!formData.user_prompt) return;
      
      setIsExtracting(true);
      try {
        const result = await extractVariables({
          system_prompt: formData.system_prompt,
          user_prompt: formData.user_prompt,
          few_shot_examples: formData.few_shot_examples
        });
        
        if (result.success) {
          setDetectedVars(result.data.variables || []);
        }
      } catch (error) {
      } finally {
        setIsExtracting(false);
      }
    };

    const timeoutId = setTimeout(extractVars, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.system_prompt, formData.user_prompt, formData.few_shot_examples]);

  return (
    <div className="wizard-step">
      <div className="step-intro">
        <h3>Write Your Prompts</h3>
        <p>Write your prompts using Jinja2 syntax for variables: <code className="inline-code">{'{{ variable_name }}'}</code></p>
      </div>

      <div className="config-fields">
        {showSystemPrompt && (
          <div className="form-group">
            <label htmlFor="system_prompt" className="form-label">
              System Prompt
              {isSystemPromptRequired && <span className="label-required">*</span>}
            </label>
            <textarea
              id="system_prompt"
              value={formData.system_prompt}
              onChange={(e) => updateFormData('system_prompt', e.target.value)}
              placeholder="e.g., You are a professional email writer with expertise in corporate communication..."
              className={`form-textarea font-mono ${isSystemPromptRequired && !formData.system_prompt.trim() ? 'form-error' : ''}`}
              rows={8}
            />
            <span className="field-hint">
              Defines the AI's role and approach
              {isSystemPromptRequired && ' (required for this prompt type)'}
            </span>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="user_prompt" className="form-label">
            User Prompt
            <span className="label-required">*</span>
          </label>
          <textarea
            id="user_prompt"
            value={formData.user_prompt}
            onChange={(e) => updateFormData('user_prompt', e.target.value)}
            placeholder="e.g., Write a professional email to {{ recipient_name }} about {{ topic }}..."
            className="form-textarea font-mono"
            rows={10}
          />
          <span className="field-hint">The main prompt with data and instructions</span>
        </div>

        {showFewShot && (
          <div className="form-group">
            <label htmlFor="few_shot" className="form-label">
              Few-Shot Examples
              <span className="label-optional">(Optional)</span>
            </label>
            <textarea
              id="few_shot"
              value={formData.few_shot_examples}
              onChange={(e) => updateFormData('few_shot_examples', e.target.value)}
              placeholder="Example 1: Input: ... Output: ..."
              className="form-textarea font-mono"
              rows={6}
            />
            <span className="field-hint">Example demonstrations for the AI</span>
          </div>
        )}

        {detectedVars.length > 0 && (
          <div className="info-box">
            <div className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
              <span>Detected Variables ({detectedVars.length})</span>
              {isExtracting && <span className="text-xs text-text-secondary">(analyzing...)</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              {detectedVars.map((varName, index) => (
                <code key={index} className="variable-tag">
                  {'{{ ' + varName + ' }}'}
                </code>
              ))}
            </div>
            <p className="text-xs text-text-secondary mt-2">
              You'll configure these in the next step
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

StepPrompts.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
};

export default StepPrompts;

